<template>
  <div>
    <v-form v-model="valid" validate-on="lazy input" ref="form">
      <v-combobox
        v-model="localValue.users"
        data-testid="add-user-modal-combobox"
        class="mt-3 coro-scrollable-combobox"
        :loading="dataLoading"
        :items="users"
        :label="$t('modals.addUsers.label')"
        :aria-label="$t('modals.addUsers.label')"
        :placeholder="$t('modals.addUsers.placeholder')"
        :rules="emailRules"
        v-model:search="currentInputValue"
        variant="outlined"
        multiple
        no-filter
        return-object
        hide-no-data
        item-value="userId"
        item-title="email"
        @update:modelValue="handleChange()"
        @blur="handleBlur()"
      >
        <template v-slot:chip="{ props, item }">
          <v-chip
            v-bind="props"
            :closable="true"
            variant="flat"
            size="default"
            :color="getChipColor(item.raw.email)"
            close-icon="$closeCircle"
            :text="item.title"
          >
            <div class="d-flex align-center">
              {{ item.raw.email }}
            </div>
          </v-chip>
        </template>
        <template #item="{ props, item }">
          <v-list-item v-bind="props" title="">
            <div class="d-flex align-center" :data-testid="`item-${camelCase(item.raw.email)}`">
              <span class="ml-1">{{ item.raw.email }}</span>
            </div>
          </v-list-item>
        </template>
      </v-combobox>

      <section v-if="!!validUsersCount">
        <div class="identified-users-wrapper mt-8 mb-8">
          <div class="d-flex align-center">
            <v-icon size="48" icon="$usersNoHexagon"></v-icon>
            <span class="identified-users-count">{{ validUsersCount }}</span>
          </div>
          <div class="font-weight-bold">{{ $t("modals.addUsers.usersIdentified") }}</div>
          <div
            v-if="protectionCapacity.totalProtectionSpots"
            :class="{ 'text-yellow-base': limitOverflow, 'text-green-dark': !limitOverflow }"
            v-html="protectionLimit"
          ></div>
        </div>
        <div
          v-if="protectionCapacity.totalProtectionSpots && limitOverflow"
          v-html="limitExceededMessage"
        ></div>
        <div v-else v-html="$t('modals.addUsers.summary', { n: validUsersCount })"></div>
      </section>
    </v-form>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import type { PropType } from "vue";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { useAddUserModal } from "@/composables/useAddUsersModal";
import { useI18n } from "vue-i18n";
import { useWorkspaceStore } from "@/_store/workspace.module";
import { storeToRefs } from "pinia";

export default defineComponent({
  props: {
    config: {
      type: Object as PropType<DialogDataConfig>,
      required: true,
    },
    modelValue: {
      type: Object,
      required: true,
    },
  },
  setup(props, { emit }) {
    const i18n = useI18n();
    const workspaceStore = useWorkspaceStore();
    const { workspace } = storeToRefs(workspaceStore);

    const onUserChanged = (nevVal: string) => {
      emit("update:localValue", nevVal);
    };

    const onValidationChange = (isValid: boolean) => {
      emit("update:valid", isValid);
    };

    const {
      users,
      form,
      valid,
      currentInputValue,
      dataLoading,
      localValue,
      emailRules,
      handleChange,
      handleBlur,
      getChipColor,
      camelCase,
      validate,
      validUsers,
    } = useAddUserModal(onUserChanged, onValidationChange);

    const validUsersCount = computed(() => validUsers.value.length);

    const protectionCapacity = computed(() => {
      const totalProtectionSpots = workspace.value.protectedUsersLimit ?? 0;
      const filledProtectionSpots = workspace.value.protectedUsers ?? 0;

      const taken = filledProtectionSpots + validUsersCount.value;
      const spots = totalProtectionSpots - taken;

      return {
        totalProtectionSpots,
        filledProtectionSpots,
        taken,
        spots,
      };
    });

    const protectionLimit = computed(() => {
      const { taken, totalProtectionSpots, spots } = protectionCapacity.value;
      return i18n.t("modals.addUsers.protectionLimit", {
        taken,
        total: totalProtectionSpots,
        spots: spots < 0 ? 0 : spots,
      });
    });

    const limitExceededMessage = computed(() => {
      return i18n.t("modals.addUsers.limitExceededMessage", {
        usersToAdd: validUsersCount.value,
        total: protectionCapacity.value.totalProtectionSpots,
      });
    });

    const limitOverflow = computed(() => protectionCapacity.value.spots <= 0);
    return {
      form,
      users,
      valid,
      validUsersCount,
      currentInputValue,
      dataLoading,
      localValue,
      emailRules,
      handleChange,
      handleBlur,
      getChipColor,
      camelCase,
      validate,
      protectionCapacity,
      protectionLimit,
      limitOverflow,
      limitExceededMessage,
    };
  },
  emits: ["update:valid", "update:localValue"],
});
</script>

<style lang="scss" scoped>
:deep(*) {
  .v-field .v-chip {
    --v-chip-height: 32px;
  }
}

.identified-users-count {
  font-size: 40px;
}

.identified-users-wrapper {
  border: 1px solid rgb(var(--v-theme-indigo-pale));
  border-radius: 8px;
  padding: 12px 8px 8px 16px;
}
</style>
